import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { SING_COLORS } from '@/constants/marketing-display.constants';
import { paymentPlansApi } from '@/services/paymentPlansApi';
import type { PaymentPlan } from '@/types/payment.types';
import { AlertCircle, CheckCircle, CreditCard, FileText, Loader2, Upload, XCircle } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { applicationsApi } from '../../api/applicationsApi';
import { SECTION_CONFIGS } from '../../constants/sectionConfig.constants';
import type { Application } from '../../types/application.types';
import { CollapsibleSection } from '../shared/CollapsibleSection';

interface PaymentConfirmationStepProps {
  application: Application;
}

const PAYMENT_COLOR = SING_COLORS.BLUE; // #00758d

export const PaymentConfirmationStep: React.FC<PaymentConfirmationStepProps> = ({ application }) => {
  const config = SECTION_CONFIGS.validation;
  const navigate = useNavigate();

  const [paymentMode, setPaymentMode] = useState<'FULL' | 'MONTHLY' | null>(null);
  const [periodMonths, setPeriodMonths] = useState<6 | 12>(12);
  const [monthNumber, setMonthNumber] = useState<number>(1);
  const [proofDocument, setProofDocument] = useState<File | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [paymentPlan, setPaymentPlan] = useState<PaymentPlan | null>(null);
  const [isLoadingPlan, setIsLoadingPlan] = useState(true);

  // Modal de clôture
  const [showCloseModal, setShowCloseModal] = useState(false);
  const [closeReason, setCloseReason] = useState('');
  const [isClosing, setIsClosing] = useState(false);

  // Charger le plan de paiement
  useEffect(() => {
    const fetchPaymentPlan = async () => {
      try {
        setIsLoadingPlan(true);
        const plan = await paymentPlansApi.getByBookingId(application.id);
        setPaymentPlan(plan);
      } catch (error) {
        console.error('[PaymentConfirmationStep] Error fetching payment plan:', error);
      } finally {
        setIsLoadingPlan(false);
      }
    };

    fetchPaymentPlan();
  }, [application.id]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const validTypes = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
      if (!validTypes.includes(file.type)) {
        setError('Format de fichier non valide. Utilisez PDF, JPG ou PNG.');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        setError('Le fichier est trop volumineux. Taille maximale : 5 MB.');
        return;
      }
      setProofDocument(file);
      setError(null);
    }
  };

  const handleSubmit = async () => {
    if (!paymentMode) {
      setError('Veuillez sélectionner le mode de paiement.');
      return;
    }
    if (!proofDocument) {
      setError('Veuillez téléverser un justificatif de paiement.');
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const data: { paymentMode: string; monthNumber: number; periodMonths?: number } = {
        paymentMode,
        monthNumber,
      };

      if (paymentMode === 'MONTHLY') {
        data.periodMonths = periodMonths;
      }

      await applicationsApi.confirmPayment(application.id, data);

      setSuccess(true);

      // Redirection après 2 secondes
      setTimeout(() => {
        navigate('/applications');
      }, 2000);
    } catch (error: any) {
      console.error('[PaymentConfirmationStep] Erreur confirmation paiement:', error);
      setError(error.response?.data?.error || 'Erreur lors de la confirmation du paiement. Veuillez réessayer.');
      setIsSubmitting(false);
    }
  };

  const handleCloseApplication = async () => {
    if (!closeReason.trim()) {
      setError('Veuillez indiquer la raison de la clôture.');
      return;
    }

    setIsClosing(true);
    setError(null);

    try {
      await applicationsApi.closeApplication(application.id, closeReason);
      setShowCloseModal(false);

      // Redirection après 1 seconde
      setTimeout(() => {
        navigate('/applications');
      }, 1000);
    } catch (error: any) {
      console.error('[PaymentConfirmationStep] Erreur clôture dossier:', error);
      setError(error.response?.data?.error || 'Erreur lors de la clôture du dossier.');
      setIsClosing(false);
    }
  };

  // Calculer les statistiques de paiement
  const paidInstallments = paymentPlan?.installments.filter((inst) => inst.status === 'PAID').length || 0;
  const totalInstallments = paymentPlan?.installments.length || 0;
  const progressPercentage = totalInstallments > 0 ? (paidInstallments / totalInstallments) * 100 : 0;
  // Dossier entièrement payé : soit toutes les échéances mensuelles sont payées,
  // soit le booking est en statut PAID (paiement intégral confirmé)
  const isFullyPaid =
    (progressPercentage === 100 && totalInstallments > 0) ||
    application.status === 'PAID' ||
    application.status === 'COMPLETED';

  // Message de succès
  if (success) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mb-6">
          <CheckCircle className="w-12 h-12 text-emerald-600" />
        </div>
        <h3 className="text-2xl font-bold text-slate-800 mb-3">Paiement confirmé !</h3>
        <p className="text-slate-600 text-center mb-6">
          Le paiement a été enregistré avec succès. Redirection en cours...
        </p>
        <Loader2 className="w-6 h-6 text-slate-400" />
      </div>
    );
  }

  // UI pour dossier entièrement payé
  if (isFullyPaid) {
    return (
      <div className="flex flex-col items-center justify-center py-12 px-4">
        <div
          className="w-24 h-24 rounded-full flex items-center justify-center mb-6"
          style={{ backgroundColor: `${PAYMENT_COLOR}20` }}
        >
          <CheckCircle className="w-16 h-16" style={{ color: PAYMENT_COLOR }} />
        </div>
        <h3 className="text-3xl font-bold text-slate-800 mb-3">Dossier entièrement payé</h3>
        <p className="text-slate-600 text-center mb-2 max-w-md">
          Tous les paiements ont été reçus et validés. Ce dossier est officiellement clôturé.
        </p>
        <p className="text-sm text-slate-500 text-center mb-8">
          Référence : <span className="font-mono font-semibold">{application.reference}</span>
        </p>

        {/* Statistiques finales */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 w-full max-w-2xl mb-8">
          <div className="bg-slate-50 rounded-lg p-4 text-center border border-slate-200">
            <div className="text-2xl font-bold text-slate-800 mb-1">{totalInstallments}</div>
            <div className="text-sm text-slate-600">Échéances payées</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 text-center border border-slate-200">
            <div className="text-2xl font-bold text-slate-800 mb-1">
              {(application.totalAmount || 0).toLocaleString('fr-FR')} FCFA
            </div>
            <div className="text-sm text-slate-600">Montant total</div>
          </div>
          <div className="bg-slate-50 rounded-lg p-4 text-center border border-slate-200">
            <div className="text-2xl font-bold" style={{ color: PAYMENT_COLOR }}>
              100%
            </div>
            <div className="text-sm text-slate-600">Progression</div>
          </div>
        </div>

        {/* Barre de progression complète */}
        <div className="w-full max-w-2xl mb-8">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-semibold text-slate-700">Progression des paiements</span>
            <span className="text-sm font-bold" style={{ color: PAYMENT_COLOR }}>
              {paidInstallments} / {totalInstallments} mois
            </span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-4 overflow-hidden">
            <div
              className="h-full rounded-full transition-all duration-500"
              style={{
                width: '100%',
                backgroundColor: PAYMENT_COLOR,
              }}
            />
          </div>
        </div>

        {/* Bouton retour */}
        <button
          onClick={() => navigate('/applications')}
          className="px-6 py-3 rounded-lg font-semibold transition-colors"
          style={{
            backgroundColor: PAYMENT_COLOR,
            color: 'white',
          }}
        >
          Retour à la liste des dossiers
        </button>
      </div>
    );
  }

  return (
    <>
      <CollapsibleSection
        title="Confirmer le paiement"
        color={PAYMENT_COLOR}
        icon={CreditCard}
        defaultOpen={config.defaultOpen}
      >
        <div className="space-y-6">
          {/* Message d'information */}
          <div
            className="border-l-4 rounded-lg p-4 flex items-start gap-3"
            style={{
              backgroundColor: `${PAYMENT_COLOR}15`,
              borderColor: PAYMENT_COLOR,
            }}
          >
            <CheckCircle className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: PAYMENT_COLOR }} />
            <div className="flex-1">
              <h4 className="text-sm font-semibold mb-1" style={{ color: PAYMENT_COLOR }}>
                Dossier validé
              </h4>
              <p className="text-sm text-slate-700">
                Le dossier a été accepté. Confirmez la réception du paiement du client (chèque ou virement bancaire) en
                téléversant le justificatif.
              </p>
            </div>
          </div>

          {/* Barre de progression pour paiements mensuels */}
          {paymentPlan && paymentPlan.paymentMode === 'MONTHLY' && totalInstallments > 0 && (
            <div
              className="border rounded-lg p-4"
              style={{ borderColor: `${PAYMENT_COLOR}30`, backgroundColor: '#f8fafc' }}
            >
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-slate-800">Progression des paiements</h4>
                <span className="text-sm font-bold" style={{ color: PAYMENT_COLOR }}>
                  {paidInstallments} / {totalInstallments} mois payés
                </span>
              </div>
              <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${progressPercentage}%`,
                    backgroundColor: PAYMENT_COLOR,
                  }}
                />
              </div>
              <p className="text-xs text-slate-600 mt-2">
                {totalInstallments - paidInstallments} mensualité
                {totalInstallments - paidInstallments > 1 ? 's' : ''} restante
                {totalInstallments - paidInstallments > 1 ? 's' : ''}
              </p>
            </div>
          )}

          {/* Sélection du mode de paiement */}
          <div className="space-y-3">
            <Label className="text-sm font-semibold text-slate-700">Mode de paiement reçu</Label>
            <div className="grid md:grid-cols-2 gap-4">
              <button
                onClick={() => setPaymentMode('FULL')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  paymentMode === 'FULL' ? 'border-[#00758d] bg-[#00758d]/5' : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMode === 'FULL' ? 'border-[#00758d]' : 'border-slate-300'
                    }`}
                  >
                    {paymentMode === 'FULL' && <div className="w-3 h-3 rounded-full bg-[#00758d]" />}
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-slate-800">Paiement intégral</p>
                    <p className="text-xs text-slate-600">Le client a payé la totalité</p>
                  </div>
                </div>
              </button>

              <button
                onClick={() => setPaymentMode('MONTHLY')}
                className={`p-4 border-2 rounded-lg transition-all ${
                  paymentMode === 'MONTHLY'
                    ? 'border-[#00758d] bg-[#00758d]/5'
                    : 'border-slate-200 hover:border-slate-300'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                      paymentMode === 'MONTHLY' ? 'border-[#00758d]' : 'border-slate-300'
                    }`}
                  >
                    {paymentMode === 'MONTHLY' && <div className="w-3 h-3 rounded-full bg-[#00758d]" />}
                  </div>
                  <div className="text-left">
                    <p className="font-semibold text-slate-800">Paiement mensuel</p>
                    <p className="text-xs text-slate-600">Le client paie par échéances</p>
                  </div>
                </div>
              </button>
            </div>
          </div>

          {/* Sélection de la période (si paiement mensuel et plan pas encore configuré) */}
          {paymentMode === 'MONTHLY' && (!paymentPlan || paymentPlan.status === 'PENDING_CHOICE') && (
            <div className="space-y-2">
              <Label htmlFor="periodMonths" className="text-sm font-semibold text-slate-700">
                Période de paiement
              </Label>
              <select
                id="periodMonths"
                value={periodMonths}
                onChange={(e) => setPeriodMonths(Number(e.target.value) as 6 | 12)}
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00758d] focus:border-transparent text-sm"
              >
                <option value={6}>6 mois</option>
                <option value={12}>12 mois</option>
              </select>
              <p className="text-xs text-slate-500">
                Choisissez la durée du plan de paiement échelonné pour ce client.
              </p>
            </div>
          )}

          {/* Sélection du mois (si paiement mensuel) */}
          {paymentMode === 'MONTHLY' && (
            <div className="space-y-2">
              <Label htmlFor="monthNumber" className="text-sm font-semibold text-slate-700">
                Mois payé
              </Label>
              {isLoadingPlan ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="w-4 h-4" />
                  Chargement des échéances...
                </div>
              ) : paymentPlan && paymentPlan.installments.length > 0 ? (
                <select
                  id="monthNumber"
                  value={monthNumber}
                  onChange={(e) => setMonthNumber(Number(e.target.value))}
                  className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#00758d] focus:border-transparent text-sm"
                >
                  {paymentPlan.installments.map((installment) => (
                    <option key={installment.id} value={installment.monthNumber}>
                      Mois {installment.monthNumber} - {installment.amount.toLocaleString('fr-FR')} FCFA
                      {installment.status === 'PAID' ? ' (Déjà payé)' : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <p className="text-sm text-slate-500">Les échéances seront générées après le premier paiement.</p>
              )}
            </div>
          )}

          {/* Upload du justificatif */}
          <div className="space-y-2">
            <Label htmlFor="proofDocument" className="text-sm font-semibold text-slate-700">
              Justificatif de paiement
            </Label>
            <div className="border-2 border-dashed border-slate-300 rounded-lg p-6 text-center hover:border-[#00758d] transition-colors">
              <input
                type="file"
                id="proofDocument"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={handleFileChange}
                className="hidden"
              />
              <label htmlFor="proofDocument" className="cursor-pointer">
                <div className="flex flex-col items-center gap-2">
                  {proofDocument ? (
                    <>
                      <FileText className="w-10 h-10 text-emerald-600" />
                      <p className="text-sm font-medium text-slate-800">{proofDocument.name}</p>
                      <p className="text-xs text-slate-500">{(proofDocument.size / 1024).toFixed(2)} KB</p>
                    </>
                  ) : (
                    <>
                      <Upload className="w-10 h-10 text-slate-400" />
                      <p className="text-sm font-medium text-slate-800">Cliquez pour téléverser</p>
                      <p className="text-xs text-slate-500">PDF, JPG ou PNG (max 5 MB)</p>
                    </>
                  )}
                </div>
              </label>
            </div>
            <p className="text-xs text-slate-500">
              Téléversez le scan du chèque, le relevé bancaire ou tout autre justificatif de paiement.
            </p>
          </div>

          {/* Message d'erreur */}
          {error && (
            <div className="border-l-4 border-red-500 bg-red-50 rounded-lg p-4 flex items-start gap-3">
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5 text-red-600" />
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Boutons d'action */}
          <div className="flex justify-between items-center pt-4 border-t border-slate-200">
            <Button
              onClick={() => setShowCloseModal(true)}
              variant="outline"
              className="px-6 py-3 border-2 border-red-200 text-red-700 hover:bg-red-50 hover:border-red-300 font-semibold rounded-lg transition-all"
            >
              <XCircle className="w-5 h-5 mr-2" />
              Clôturer le dossier
            </Button>

            <Button
              onClick={handleSubmit}
              disabled={isSubmitting || !paymentMode || !proofDocument}
              className="px-8 py-3 bg-[#00758d] hover:bg-[#006374] text-white font-semibold rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2" />
                  Confirmation en cours...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Confirmer le paiement
                </>
              )}
            </Button>
          </div>
        </div>
      </CollapsibleSection>

      {/* Modal de clôture */}
      {showCloseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center">
                <XCircle className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-800">Clôturer le dossier</h3>
            </div>

            <p className="text-sm text-slate-600 mb-4">
              Cette action marquera le dossier comme clôturé. Veuillez indiquer la raison de cette clôture.
            </p>

            <div className="space-y-2 mb-6">
              <Label htmlFor="closeReason" className="text-sm font-semibold text-slate-700">
                Raison de la clôture
              </Label>
              <textarea
                id="closeReason"
                value={closeReason}
                onChange={(e) => setCloseReason(e.target.value)}
                placeholder="Ex: Client a arrêté son abonnement, Non-paiement persistant, etc."
                className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-sm resize-none"
                rows={4}
              />
            </div>

            <div className="flex gap-3">
              <Button
                onClick={() => {
                  setShowCloseModal(false);
                  setCloseReason('');
                  setError(null);
                }}
                variant="outline"
                className="flex-1 px-4 py-3 border-2 border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold rounded-lg"
                disabled={isClosing}
              >
                Annuler
              </Button>
              <Button
                onClick={handleCloseApplication}
                disabled={isClosing || !closeReason.trim()}
                className="flex-1 px-4 py-3 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isClosing ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2" />
                    Clôture...
                  </>
                ) : (
                  'Confirmer la clôture'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
